import { Button } from '@renderer-ui-library/atoms'
import { Container, Grid } from '@renderer-ui-library/mui'
import { useMediaQuery } from '@renderer-ui-library/mui/utils/mediaQuery/useMediaQuery'
import { localeNamespace } from '@internal/utils/machine/i18n/Locale'
import { TLocale } from '@internal/utils/machine/i18n/TLocale'
import classNames from 'classnames'
import { translations } from 'i18n/translations'
import { useTranslation } from 'next-i18next'
import dynamic from 'next/dynamic'
import React, { useCallback, useContext } from 'react'
import { createPortal } from 'react-dom'
import { useMachineComparisonRouting } from 'utils/routing/useMachineComparisonRouting'
import { tracker } from 'utils/tracking/tracker'
import { CompareMachinesItem } from './CompareMachinesItem'
import { MachineComparisonContext } from './MachineComparisonContext'
import styles from './compareMachinesOverlay.module.scss'
interface CompareMachinesOverlayProps {
  locale: TLocale
}

export const CompareMachinesOverlay: React.FC<CompareMachinesOverlayProps> =
  React.memo((props) => {
    const { t } = useTranslation(localeNamespace.common)
    const { isMobile } = useMediaQuery()

    const {
      maxMachinesToCompare,
      isCompareModeEnabled,
      machines,
      removeMachine,
      leaveCompareMode,
      readyForComparison,
    } = useContext(MachineComparisonContext)
    const { gotoCompareMachinesPage } = useMachineComparisonRouting(
      props.locale
    )

    const handleCompareClick = useCallback(() => {
      tracker.trackEvent({
        name: 'click',
        target_name: 'button.serp.compare_machines.submit',
        is_interaction: true,
      })

      gotoCompareMachinesPage(
        machines.map(({ machineGroupId }) => machineGroupId)
      )
    }, [gotoCompareMachinesPage, machines])

    return createPortal(
      <div
        className={classNames(styles.compareWrapper, {
          [styles.open]: isCompareModeEnabled,
        })}
      >
        <Container disableGutters maxWidth='lg'>
          <div className={styles.compareContainer}>
            <div className={styles.machines}>
              <Grid container columnSpacing={2}>
                {machines.map((machine) => (
                  <Grid item xs={12} md={4} key={machine.machineGroupId}>
                    <CompareMachinesItem
                      locale={props.locale}
                      machine={machine}
                      onRemoveClick={removeMachine}
                    />
                  </Grid>
                ))}
              </Grid>
            </div>
            <div className={styles.buttons}>
              <div className={styles.button}>
                <Button
                  variant='outlined'
                  onClick={leaveCompareMode}
                  className={styles.button}
                  noMinWidth
                  fullWidth
                >
                  {t(translations.WizardCancelButtonLabel)}
                </Button>
              </div>
              <div className={styles.button}>
                <Button
                  variant='contained'
                  color='secondary'
                  onClick={handleCompareClick}
                  disabled={!readyForComparison}
                  noMinWidth
                  fullWidth
                >
                  {t(translations.compareMachinesSearchResultLabel)}
                  {isMobile && ` ${machines.length}/${maxMachinesToCompare}`}
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </div>,
      document.body
    )
  })

CompareMachinesOverlay.displayName = 'CompareMachinesOverlay'
